import { ComponentStore } from '@ngrx/component-store';
import { Injectable } from '@angular/core';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import {
	Bid,
	RestService,
	IS_NULL_OR_UNDEFINED,
	IS_NOT_EMPTY,
	BID_STATUS_STRING,
	BidResponseList,
	BID_STATUS_UUID,
	QUOTE_STATUS,
} from 'cargo-chief-commons';
import {
	EMPTY,
	Observable,
	switchMap,
	withLatestFrom,
	debounceTime,
	of,
	exhaustMap,
	Subject,
} from 'rxjs';
import { UrlService } from '@service/url/url.service';
import { catchError, tap, filter, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ErrorActions } from '@state/error';
import { PushNotificationV2Service } from '../../services/push-notification/push-notification-v2.service';
import { BidListConfig } from '../models/bid-list-config.interface';
import moment from 'moment';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { OFFER_TAB_TYPES } from '../../offers/components/offers-list/constants';
import { FILTER_TYPES } from '@domain/constants/filter-types';

// CN-8620: Status filter for the quote bid list (multi-select + is / is not).
export interface QuoteStatusFilter {
	// Display status strings (e.g. 'Contacted', 'Declined') selected by the user.
	statuses: string[];
	// 'is' -> statusList (IN), 'isNot' -> excludedStatusList (NOT IN).
	mode: 'is' | 'isNot';
}

interface BidsState {
	loading: boolean;
	bids: EntityState<Bid>;
	initialBidsListLoaded: boolean;
	pageNumber: number;
	pageSize: number;
	totalElements: number;
	hasNext: boolean;
	offerUpdatesAvailable: boolean;
	currentConfig: BidListConfig | null;
	allTabs: string[];
	activeTab: string;
	userSortAndFilters: any[];
	customTabId: number | null;
	// CN-8620: status filter applied to the quote bid list.
	quoteStatusFilter: QuoteStatusFilter;
}

// CN-8621: status.id UUID for DECLINED (see BID_STATUS_UUID). Declined bids are excluded from the
// Quote Details bid list — Decline is a menu-only action, so a declined bid should drop out of view.
const DECLINED_BID_STATUS_UUID = '269e4734-6ade-44a3-952b-6e619d0ac14b';

const statusPriority: { [key: string]: number } = {
	SELECTED: 1,
	ACCEPTED: 2,
	COUNTERED: 3,
	RESPONSE_RECEIVED: 4,
	CONTACTED: 5,
	VIEWED: 6,
	INVITED: 7,
	DAILY_LIMIT_REACHED: 8,
	DECLINED: 9,
	EXPIRED: 10,
	CANCELLED: 11,
	SCHEDULED: 12,
	NEW: 13,
	ASSIGNED: 14,
	DRAFT: 15,
	SIGNED: 16,
};

const sortComparer = (a: Bid, b: Bid): number => {
	const priorityA = statusPriority[a.status] || Infinity;
	const priorityB = statusPriority[b.status] || Infinity;

	if (priorityA !== priorityB) {
		return priorityA - priorityB;
	}

	const lastModifiedA = Number(a.lastModified) || 0;
	const lastModifiedB = Number(b.lastModified) || 0;
	if (lastModifiedA !== lastModifiedB) {
		return lastModifiedB - lastModifiedA; // Descending order (most recent first)
	}

	const createdA = Number(a.created) || 0;
	const createdB = Number(b.created) || 0;
	return createdB - createdA; // Descending order (most recent first)
};

const adapter = createEntityAdapter<Bid>({ sortComparer });
const { selectAll } = adapter.getSelectors();

const initialState: BidsState = {
	loading: false,
	bids: adapter.getInitialState(),
	initialBidsListLoaded: false,
	pageNumber: 0,
	pageSize: 15,
	totalElements: 0,
	hasNext: false,
	offerUpdatesAvailable: false,
	currentConfig: null,
	allTabs: [OFFER_TAB_TYPES.ACTIVE],
	activeTab: OFFER_TAB_TYPES.ACTIVE,
	userSortAndFilters: [],
	customTabId: null,
	quoteStatusFilter: { statuses: [], mode: 'is' },
};

@Injectable({ providedIn: 'root' })
export class BidsStore extends ComponentStore<BidsState> {
	lastBidsListFetchedAt: Date = null;
	uniqueBidIds: string[] = [];

	private readonly errorMessage = 'Failed to load bids.';
	private readonly bidCountCache = new Map<string, { count: number; timestamp: number }>();
	private readonly CACHE_DURATION = 5000;
	public customTabId: number;
	private _customTabDeleted = new Subject<void>();
	public readonly customTabDeleted$ = this._customTabDeleted.asObservable();

	constructor(
		private readonly store: Store,
		private readonly urlService: UrlService,
		private readonly restService: RestService,
		private readonly pushNotificationService: PushNotificationV2Service,
		private readonly cargochief: Cargochief
	) {
		super(initialState);

		// Make debug functions globally accessible for browser console testing
		(window as any).debugBidsStore = {
			testApi: () => this.debugTestApi(),
			logState: () => this.debugLogState(),
			compareWithPostman: () => this.debugCompareWithPostman(),
		};
		// Fire the “fetch once” effect whenever currentConfig becomes 'global' (Offers page)
		this.getUserSortsAndFilters(this.currentConfig$);
	}

	public loading$ = this.select((state) => state.loading);
	public initialBidsListLoaded$ = this.select((state) => state.initialBidsListLoaded);

	public bids$ = this.select(
		this.select((state) => state.bids),
		selectAll
	);

	public pageNumber$ = this.select((state) => state.pageNumber);
	public pageSize$ = this.select((state) => state.pageSize);
	public totalElements$ = this.select((state) => state.totalElements);
	public hasNext$ = this.select((state) => state.hasNext);
	public offerUpdatesAvailable$ = this.select((state) => state.offerUpdatesAvailable);
	public currentConfig$ = this.select((state) => state.currentConfig);
	public allTabs$ = this.select((state) => state.allTabs);
	public activeTab$ = this.select((state) => state.activeTab);
	public userSortAndFilters$ = this.select((state) => state.userSortAndFilters);
	public customTabId$ = this.select((state) => state.customTabId);
	public quoteStatusFilter$ = this.select((state) => state.quoteStatusFilter);

	// Generalized load effect that works with different contexts
	public readonly loadBids = this.effect((config$: Observable<BidListConfig>) => {
		return config$.pipe(
			debounceTime(100),
			tap((config) => {
				this.setLoading(true);
				this.setCurrentConfig(config);
			}),
			withLatestFrom(this.pageNumber$, this.pageSize$),
			switchMap(([config, storePageNumber, storePageSize]) => {
				const pageNumber = config.pageNumber !== undefined ? config.pageNumber : storePageNumber;
				const pageSize = config.pageSize || storePageSize;
				const url = this.getUrlForContext(config, pageNumber, pageSize);
				const payload = this.getPayloadForContext(config);
				return this.restService.POST(url, payload).pipe(
					tap({
						next: (response: any) => {
							const bidResponseList = this.mapApiResponseToBidResponseList(response);
							this.uniqueBidIds = [
								...new Set(this.uniqueBidIds.concat(bidResponseList.bids.map((bid) => bid.id))),
							];
							this.resetBids({
								bids: bidResponseList.bids,
								hasNext: bidResponseList.hasNext,
								totalElements: bidResponseList.totalElements,
							});
							this.patchState((state) => ({
								...state,
								initialBidsListLoaded: true,
							}));
						},
						error: (error) => {
							this.store.dispatch(
								ErrorActions.error.show({ payload: { message: this.getErrorMessage(config) } })
							);
						},
					}),
					catchError((error) => {
						return EMPTY;
					})
				);
			}),
			tap(() => {
				this.setLoading(false);
			})
		);
	});

	// Legacy method for quote-based loading (for backward compatibility)
	public readonly loadBidsForQuote = this.effect((quoteId$: Observable<string>) => {
		return quoteId$.pipe(
			tap(() => this.setLoading(true)),
			withLatestFrom(this.pageNumber$, this.pageSize$),
			switchMap(([quoteId, pageNumber, pageSize]) => {
				const config: BidListConfig = {
					context: 'quote',
					quoteId,
					// CN-8616: thread the resolved page into the config so /bids/list paging
					// (now in the request body) reflects the requested page.
					pageNumber,
					pageSize: pageSize,
				};
				this.setCurrentConfig(config);

				const url = this.getUrlForContext(config, pageNumber, pageSize);
				const payload = this.getPayloadForContext(config);

				return this.restService.POST(url, payload).pipe(
					tap({
						// CN-8616: /bids/list wraps the list as { code, data } — normalize it.
						next: (response: any) => {
							const bidResponseList = this.mapApiResponseToBidResponseList(response);
							this.uniqueBidIds = [
								...new Set(this.uniqueBidIds.concat(bidResponseList.bids.map((bid) => bid.id))),
							];
							this.resetBids({
								bids: bidResponseList.bids,
								hasNext: bidResponseList.hasNext,
								totalElements: bidResponseList.totalElements,
							});
							this.patchState((state) => ({
								...state,
								initialBidsListLoaded: true,
							}));
						},
						error: () =>
							this.store.dispatch(
								ErrorActions.error.show({ payload: { message: this.getErrorMessage(config) } })
							),
					}),
					catchError(() => EMPTY)
				);
			}),
			tap(() => this.setLoading(false))
		);
	});

	// Method that accepts explicit page number to ensure proper pagination
	public readonly loadBidsForQuoteWithPage = this.effect(
		(input$: Observable<{ quoteId: string; pageNumber: number }>) => {
			return input$.pipe(
				tap(() => this.setLoading(true)),
				withLatestFrom(this.pageSize$),
				switchMap(([{ quoteId, pageNumber }, pageSize]) => {
					const config: BidListConfig = {
						context: 'quote',
						quoteId,
						// CN-8616: carry the explicitly requested page into the config so the
						// /bids/list body paging fetches the right page (not store state).
						pageNumber,
						pageSize: pageSize,
					};
					this.setCurrentConfig(config);

					const url = this.getUrlForContext(config, pageNumber, pageSize);
					const payload = this.getPayloadForContext(config);

					return this.restService.POST(url, payload).pipe(
						tap({
							// CN-8616: /bids/list wraps the list as { code, data } — normalize it.
							next: (response: any) => {
								const bidResponseList = this.mapApiResponseToBidResponseList(response);
								this.uniqueBidIds = [
									...new Set(this.uniqueBidIds.concat(bidResponseList.bids.map((bid) => bid.id))),
								];
								this.resetBids({
									bids: bidResponseList.bids,
									hasNext: bidResponseList.hasNext,
									totalElements: bidResponseList.totalElements,
								});
								this.patchState((state) => ({
									...state,
									initialBidsListLoaded: true,
								}));
							},
							error: () =>
								this.store.dispatch(
									ErrorActions.error.show({ payload: { message: this.getErrorMessage(config) } })
								),
						}),
						catchError(() => EMPTY)
					);
				}),
				tap(() => this.setLoading(false))
			);
		}
	);

	public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));

	public readonly setCurrentConfig = this.updater((state, config: BidListConfig) => ({
		...state,
		currentConfig: config,
	}));

	public readonly setInitialStateForBidsList = this.updater((state) => ({
		...state,
		bids: adapter.getInitialState(),
		currentConfig: null,
	}));

	public readonly setPageNumber = this.updater((state, pageNumber: number) => ({
		...state,
		pageNumber,
	}));

	public readonly resetBids = this.updater((state, bidResponseList: BidResponseList) => ({
		...state,
		bids: adapter.setAll(bidResponseList.bids, state.bids),
		hasNext: bidResponseList.hasNext,
		totalElements: bidResponseList.totalElements,
	}));

	public readonly updateBid = this.updater(
		(state, changes: { id: string; changes: Partial<Bid>; quoteLane?: string }) => {
			const currentBid = state?.bids?.entities[changes.id];

			if (
				changes?.changes?.status === 'VIEWED' &&
				currentBid?.status !== 'NEW' &&
				currentBid?.status !== 'INVITED'
			) {
				return { ...state };
			}
			if (!currentBid) {
				this.lastBidsListFetchedAt = null;
			}
			if (
				IS_NOT_EMPTY(changes?.changes?.status) &&
				IS_NOT_EMPTY(currentBid?.status) &&
				changes?.changes?.status !== currentBid?.status
			) {
				currentBid.status = changes.changes.status;
			}

			// CN-8475: notify off a bid that reflects the incoming change (freshest bidHistories/status),
			// so the notification can attribute the counter/accept to whoever actually acted last.
			if (currentBid) {
				this.pushNotificationService.sendBidNotification(
					changes?.changes?.status,
					{ ...currentBid, ...changes.changes } as Bid,
					changes?.quoteLane || ''
				);
			}

			return {
				...state,
				bids: adapter.updateOne({ ...changes, changes: { ...changes.changes } }, state.bids),
			};
		}
	);

	public readonly updateAllBids = this.updater((state, changes: Partial<Bid>) => ({
		...state,
		bids: adapter.map((bid) => ({ ...bid, ...changes }), state.bids),
	}));

	// CN-8621: drop a bid from the list entirely (e.g. after Decline) rather than marking it DECLINED
	// in place — the Quote Details list never shows declined bids.
	public readonly removeBid = this.updater((state, bidId: string) => {
		const existed = !!state.bids.entities[bidId];

		return {
			...state,
			bids: adapter.removeOne(bidId, state.bids),
			totalElements: existed
				? Math.max(0, (state.totalElements ?? 0) - 1)
				: state.totalElements ?? 0,
		};
	});

	public readonly setOfferUpdatesAvailable = this.updater(
		(state, offerUpdatesAvailable: boolean) => ({
			...state,
			offerUpdatesAvailable,
		})
	);

	// should not trigger multiple api calls
	public readonly updateTotalBidsCount = this.effect((config$: Observable<BidListConfig>) => {
		return config$.pipe(
			debounceTime(this.CACHE_DURATION),
			switchMap((config) => {
				const cacheKey = this.getCacheKey(config);
				if (this.isCacheValid(cacheKey)) {
					const cached = this.bidCountCache.get(cacheKey);
					this.patchState((state) => ({
						...state,
						totalElements: cached.count,
					}));
					return EMPTY;
				}

				const url = this.getUrlForContext(config, 0, 1);
				const payload = this.getPayloadForContext(config);

				return this.restService.POST(url, payload).pipe(
					tap({
						next: (response: any) => {
							// Handle different response structures for total count
							let totalElements: number;

							if (response.data && response.data.totalElements !== undefined) {
								// New API response structure (bids/list endpoint) - data is nested
								totalElements = response.data.totalElements;
							} else if (response.data && response.numberOfResults !== undefined) {
								// Alternative new API response structure
								totalElements = response.numberOfResults;
							} else if (response.totalElements !== undefined) {
								// Legacy API response structure
								totalElements = response.totalElements;
							} else {
								// Fallback
								totalElements =
									response.data?.totalElements ||
									response.numberOfResults ||
									response.totalElements ||
									0;
							}

							this.bidCountCache.set(cacheKey, {
								count: totalElements,
								timestamp: Date.now(),
							});
							this.patchState((state) => ({
								...state,
								totalElements: totalElements,
							}));
						},
						error: () => this.handleBidsError(),
					}),
					catchError(() => this.handleBidsError())
				);
			})
		);
	});

	public readonly clear = this.updater(() => ({ ...initialState }));

	private getUrlForContext(config: BidListConfig, pageNumber?: number, pageSize?: number): string {
		// Use page number from config if provided, otherwise use passed pageNumber, otherwise use store page number
		const page =
			config.pageNumber !== undefined
				? config.pageNumber
				: (pageNumber ?? this.get((state) => state.pageNumber));
		const size = pageSize ?? config.pageSize ?? this.get((state) => state.pageSize);

		switch (config.context) {
			case 'quote':
				// CN-8616: the quote bid list uses the enriched /bids/list endpoint
				// (GetBidsListEnriched) so it can return carrier contact data (F3),
				// honor the status filter (F4) and hide declined bids (F5) — none of
				// which the legacy /bids (GetBidsForQuote) path supports. Paging moves
				// into the payload (pageNumber/pageSize), so the page args are unused here.
				return this.urlService.getBidsList();
			case 'carrier':
				return this.urlService.getCarriersBidList(page, size, Number(config.accountId));
			case 'global':
				// For global context (broker offers), use the bids/list endpoint
				// This should return all bids for the current account's loads
				return this.urlService.getBidsList();
			default:
				return this.urlService.getBidsList();
		}
	}

	private convertStatusToUUID(status: string): string {
		// First, check if the status is a direct key in BID_STATUS_UUID (like 'RESPONSE_RECEIVED')
		if (BID_STATUS_UUID[status]) {
			// If it's a direct key, return the UUID that maps to the display value
			for (const [uuid, displayValue] of Object.entries(BID_STATUS_UUID)) {
				if (displayValue === BID_STATUS_UUID[status]) {
					return uuid;
				}
			}
		}

		// Then check for case-insensitive match with display values
		for (const [uuid, statusValue] of Object.entries(BID_STATUS_UUID)) {
			if (statusValue.toLowerCase() === status.toLowerCase()) {
				return uuid;
			}
		}

		// Finally, check BID_DISPLAY_STATUS_CONSTANTS for additional mappings
		const displayConstants = {
			NEW: 'New',
			INVITED: 'Invited',
			ACCEPTED: 'Accepted',
			DECLINED: 'Declined',
			COUNTERED: 'Countered',
			VIEWED: 'Viewed',
			CANCELLED: 'Cancelled',
			EXPIRED: 'Expired',
			SELECTED: 'Selected',
			RESPONSE_RECEIVED: 'Responded',
			ASSIGNED: 'Assigned',
			SCHEDULED: 'Scheduled',
			CONTACTED: 'Contacted',
		};

		const displayValue = displayConstants[status];
		if (displayValue) {
			for (const [uuid, statusValue] of Object.entries(BID_STATUS_UUID)) {
				if (statusValue.toLowerCase() === displayValue.toLowerCase()) {
					return uuid;
				}
			}
		}

		return null;
	}

	private getPayloadForContext(config: BidListConfig): any {
		switch (config.context) {
			case 'quote': {
				// CN-8616: /bids/list (GetBidsListEnriched) is scoped by quoteIds and
				// returns carrier (incl. contact) and quote enrichment.
				const quotePayload: any = {
					quoteIds: [config.quoteId],
					includeCarrierData: true,
					// F3 (CN-8619): carrier phone/email for the invite call list.
					includeContactData: true,
					// CN-8764: do NOT request truck-lane enrichment. It calls the legacy
					// porus /carrier/-/truck/lane/list endpoint, which 504s in prod (~150/day)
					// and makes the bid list hang/time out. The only field it fed was the
					// Truck Location column, which has been removed from this list.
					includeTruckLaneData: false,
					includeQuoteData: true,
					// ignoreStatus keeps NEW bids in the list so the quote bid list still
					// shows every bid like the legacy /bids path; GetBidsListEnriched
					// otherwise excludes NEW by default.
					ignoreStatus: true,
					// CN-8621: Decline is a menu-only action on the Quote Details bid list — declined
					// bids must never appear in it (status.id NOT IN [DECLINED]).
					excludedStatusList: [DECLINED_BID_STATUS_UUID],
				};
				// Paging moves into the payload for /bids/list. Resolve from the config,
				// falling back to the store's current page/size.
				const pageNumber =
					config.pageNumber !== undefined
						? config.pageNumber
						: this.get((state) => state.pageNumber);
				const pageSize = config.pageSize ?? this.get((state) => state.pageSize);
				if (typeof pageNumber === 'number') {
					quotePayload.pageNumber = pageNumber;
				}
				if (typeof pageSize === 'number') {
					quotePayload.pageSize = pageSize;
				}
				// CN-8620: apply the status filter (is / is not), composing with the
				// baseline excludedStatusList rather than overwriting it.
				return this.applyQuoteStatusFilter(quotePayload);
			}
			case 'carrier':
				return {
					accountId: config.accountId,
					...(config.filters || {}),
				};
			case 'global':
				// For global context (broker offers), construct payload matching ListBidsRequest
				const payload: any = {
					accountIds: [this.cargochief.ACCOUNT_ID],
					includeQuoteData: true, // Include quote data for offers table
					includeCarrierData: true, // Include carrier data for offers table
					includeCustomerData: true, // Include customer data for offers table
					// CN-8940: carrier contact name/phone/email for the contact controls in
					// the carrier-name cell. On the c4 side this only flips `withoutContacts`
					// on the carrier-hub batch call the list already makes — no extra request.
					includeContactData: true,
					includeTruckLaneData: false, // Disable truck lane data to avoid 414 error for offers list
				};

				// Build filtersList for dynamic filtering
				const filtersList = this.buildFiltersListFromConfig(config);

				// Add filtersList to payload if we have any filters
				if (filtersList.length > 0) {
					payload.filtersList = filtersList;
				}

				// Add sortList to payload if sorting is configured
				if (config.sortList && config.sortList.length > 0) {
					payload.sortList = config.sortList;
				} else {
					// Default sort by status first (business priority), then lastModified descending (most recent first)
					payload.sortList = [
						{
							field: 'status',
							direction: 'ASC',
						},
						{
							field: 'lastModified',
							direction: 'DESC',
						},
					];
				}

				// Add carrierId from config if it exists
				if (config.carrierId) {
					payload.carrierAccountId = config.carrierId;
				}

				// Add paging if present
				if (typeof config.pageNumber === 'number') {
					payload.pageNumber = config.pageNumber;
				}
				if (typeof config.pageSize === 'number') {
					payload.pageSize = config.pageSize;
				}

				return payload;
			default:
				return config.filters || {};
		}
	}

	// CN-8620: apply the quote bid list status filter to the payload.
	// 'is' -> statusList (IN); 'isNot' -> excludedStatusList (NOT IN), merged with
	// any baseline excludedStatusList already present on the payload so a separate
	// default exclusion (e.g. DECLINED) is preserved rather than overwritten.
	private applyQuoteStatusFilter(payload: any): any {
		const filter = this.get((state) => state.quoteStatusFilter);

		if (!filter || !filter.statuses?.length) {
			return payload;
		}

		const statusUuids = filter.statuses
			.map((status) => this.convertStatusToUUID(status))
			.filter((uuid) => uuid !== null);

		if (!statusUuids.length) {
			return payload;
		}

		if (filter.mode === 'isNot') {
			const existing: string[] = Array.isArray(payload.excludedStatusList)
				? payload.excludedStatusList
				: [];
			payload.excludedStatusList = [...new Set([...existing, ...statusUuids])];
		} else {
			const existing: string[] = Array.isArray(payload.statusList) ? payload.statusList : [];
			payload.statusList = [...new Set([...existing, ...statusUuids])];
		}

		return payload;
	}

	private buildFiltersListFromConfig(config: BidListConfig): any[] {
		const filtersList: any[] = [];

		if (!config.filters) {
			return filtersList;
		}

		this.addCustomerFilter(filtersList, config.filters);
		this.addLocationFilters(filtersList, config.filters);
		this.addDateFilters(filtersList, config.filters);
		this.addStatusFilter(filtersList, config.filters);
		this.addQuoteStatusFilter(filtersList, config.filters);
		this.addUserFilter(filtersList, config.filters);
		this.addReferenceNumberFilter(filtersList, config.filters);
		this.addCarrierBusinessNameFilter(filtersList, config.filters);
		this.addCarrierMcFilter(filtersList, config.filters);
		this.addCarrierDotFilter(filtersList, config.filters);
		this.addLoadRateFilter(filtersList, config.filters);
		this.addBidRateFilter(filtersList, config.filters);
		this.addEquipmentFilter(filtersList, config.filters);
		this.addCreatedDateFilter(filtersList, config.filters);
		this.addDeliveryDateFilter(filtersList, config.filters);
		this.addPickupDateFilter(filtersList, config.filters);

		return filtersList;
	}

	private addCustomerFilter(filtersList: any[], filters: any): void {
		if (!filters.customer) {
			return;
		}

		const customerFilter = filters.customer;
		// Extract customerCode from the customer object
		let customerCode = '';
		if (typeof customerFilter.value === 'object' && customerFilter.value.customerCode) {
			customerCode = customerFilter.value.customerCode;
		} else if (typeof customerFilter.value === 'string') {
			customerCode = customerFilter.value;
		}

		if (customerCode) {
			filtersList.push({
				field: 'customerName',
				value: customerCode, // Send the customerCode string, not the object
				operator: customerFilter.operator || '=',
				startValue: customerFilter.startValue,
				endValue: customerFilter.endValue,
				condition: customerFilter.filterCondition || 'and',
			});
		}
	}

	private addLocationFilters(filtersList: any[], filters: any): void {
		// Handle origin city filtering
		if (filters.originCity) {
			const originCityFilter = filters.originCity;
			filtersList.push({
				field: 'originCity',
				value: originCityFilter.value,
				operator: originCityFilter.operator || '%',
				startValue: originCityFilter.startValue,
				endValue: originCityFilter.endValue,
				condition: originCityFilter.filterCondition || 'and',
			});
		}

		// Handle origin state filtering
		if (filters.originState) {
			const originStateFilter = filters.originState;
			filtersList.push({
				field: 'originState',
				value: originStateFilter.value,
				operator: originStateFilter.operator || '=',
				startValue: originStateFilter.startValue,
				endValue: originStateFilter.endValue,
				condition: originStateFilter.filterCondition || 'and',
			});
		}

		// Handle destination city filtering
		if (filters.destinationCity) {
			const destinationCityFilter = filters.destinationCity;
			filtersList.push({
				field: 'destinationCity',
				value: destinationCityFilter.value,
				operator: destinationCityFilter.operator || '%',
				startValue: destinationCityFilter.startValue,
				endValue: destinationCityFilter.endValue,
				condition: destinationCityFilter.filterCondition || 'and',
			});
		}

		// Handle destination state filtering
		if (filters.destinationState) {
			const destinationStateFilter = filters.destinationState;
			filtersList.push({
				field: 'destinationState',
				value: destinationStateFilter.value,
				operator: destinationStateFilter.operator || '=',
				startValue: destinationStateFilter.startValue,
				endValue: destinationStateFilter.endValue,
				condition: destinationStateFilter.filterCondition || 'and',
			});
		}
	}

	private addDateFilters(filtersList: any[], filters: any): void {
		// Handle start date filtering
		if (filters.startDate) {
			const startDateFilter = filters.startDate;
			filtersList.push({
				field: 'startDate',
				value: startDateFilter.value,
				operator: startDateFilter.operator || 'between',
				startValue: startDateFilter.startValue,
				endValue: startDateFilter.endValue,
				condition: startDateFilter.filterCondition || 'and',
			});
		}

		// Handle end date filtering
		if (filters.endDate) {
			const endDateFilter = filters.endDate;
			filtersList.push({
				field: 'endDate',
				value: endDateFilter.value,
				operator: endDateFilter.operator || 'between',
				startValue: endDateFilter.startValue,
				endValue: endDateFilter.endValue,
				condition: endDateFilter.filterCondition || 'and',
			});
		}
	}

	private addStatusFilter(filtersList: any[], filters: any): void {
		if (!filters.status) {
			return;
		}

		const statusFilter = filters.status;

		// Convert status display strings to UUIDs
		const statusList = statusFilter.value
			.map((status) => {
				const uuid = this.convertStatusToUUID(status);
				return uuid;
			})
			.filter((uuid) => uuid !== null);

		if (statusList.length > 0) {
			// Add status filter to filtersList instead of payload.statusList
			filtersList.push({
				field: 'status',
				value: statusList.join(','), // Join UUIDs as comma-separated string
				operator: statusFilter.operator || '=',
				startValue: statusFilter.startValue,
				endValue: statusFilter.endValue,
				condition: statusFilter.filterCondition || 'and',
			});
		}
	}

	private addQuoteStatusFilter(filtersList: any[], filters: any): void {
		if (!filters.quoteStatus) {
			return;
		}

		const quoteStatusFilter = filters.quoteStatus;
		// Convert quoteStatus display strings to UUIDs using QUOTE_STATUS enum
		const quoteStatusList = quoteStatusFilter.value
			.map((status) => this.convertQuoteStatusToUUID(status))
			.filter((uuid) => uuid !== null);

		if (quoteStatusList.length > 0) {
			// Add quoteStatus filter to filtersList
			filtersList.push({
				field: 'quoteStatus',
				value: quoteStatusList.join(','), // Join UUIDs as comma-separated string
				operator: quoteStatusFilter.operator || '=',
				startValue: quoteStatusFilter.startValue,
				endValue: quoteStatusFilter.endValue,
				condition: quoteStatusFilter.filterCondition || 'and',
			});
		}
	}

	private convertQuoteStatusToUUID(status: string): string {
		// Find the UUID for the given quote status string
		for (const [uuid, statusValue] of Object.entries(QUOTE_STATUS)) {
			if (statusValue.toLowerCase() === status.toLowerCase()) {
				return uuid;
			}
		}
		return null;
	}

	private getCacheKey(config: BidListConfig): string {
		return `${config.context}-${config.quoteId || config.accountId || 'global'}`;
	}

	private isCacheValid(cacheKey: string): boolean {
		const cached = this.bidCountCache.get(cacheKey);
		return cached && Date.now() - cached.timestamp < this.CACHE_DURATION;
	}

	private handleBidsError() {
		this.store.dispatch(ErrorActions.error.show({ payload: { message: this.errorMessage } }));
		return EMPTY;
	}

	private getErrorMessage(config: BidListConfig): string {
		switch (config.context) {
			case 'quote':
				return `Failed to load carrier offers for quote ${config.quoteId}.`;
			case 'carrier':
				return `Failed to load offers for carrier ${config.accountId || config.carrierId}.`;
			default:
				return 'Failed to load carrier offers.';
		}
	}

	private mapApiResponseToBidResponseList(response: any): BidResponseList {
		// Handle different response structures
		if (response.data && response.data.bids && response.data.totalElements !== undefined) {
			// New API response structure (bids/list endpoint) - data is nested
			return {
				bids: response.data.bids,
				hasNext: response.data.hasNext || false,
				totalElements: response.data.totalElements,
			};
		} else if (response.data && response.numberOfResults !== undefined) {
			// Alternative new API response structure
			return {
				bids: response.data,
				hasNext: response.hasNext || false,
				totalElements: response.numberOfResults,
			};
		} else if (response.bids && response.totalElements !== undefined) {
			// Legacy API response structure
			return response;
		} else if (response.data && Array.isArray(response.data)) {
			// Response where data is directly an array of bids
			return {
				bids: response.data,
				hasNext: response.hasNext || false,
				totalElements: response.data.length,
			};
		} else if (response.data && typeof response.data === 'object') {
			// Response where data is an object but we need to extract bids
			// Try to find bids in the data object
			let bids = [];
			let totalElements = 0;
			let hasNext = false;

			// Check if data has a content array (common pagination structure)
			if (response.data.content && Array.isArray(response.data.content)) {
				bids = response.data.content;
				totalElements =
					response.data.totalElements || response.data.total || response.data.content.length;
				hasNext = response.data.hasNext || response.data.last === false;
			} else if (response.data.bids && Array.isArray(response.data.bids)) {
				bids = response.data.bids;
				totalElements =
					response.data.totalElements || response.data.total || response.data.bids.length;
				hasNext = response.data.hasNext || false;
			} else if (response.data.elements && Array.isArray(response.data.elements)) {
				bids = response.data.elements;
				totalElements =
					response.data.totalElements || response.data.total || response.data.elements.length;
				hasNext = response.data.hasNext || false;
			} else {
				// If no recognizable structure, try to treat the entire data as bids
				bids = [response.data];
				totalElements = 1;
				hasNext = false;
			}

			return {
				bids: bids,
				hasNext: hasNext,
				totalElements: totalElements,
			};
		} else {
			// Fallback for other response structures
			return {
				bids: response.data?.bids || response.data || response.bids || [],
				hasNext: response.data?.hasNext || response.hasNext || false,
				totalElements:
					response.data?.totalElements || response.numberOfResults || response.totalElements || 0,
			};
		}
	}

	// Debug method to test API directly
	public debugTestApi(): void {
		const testConfig: BidListConfig = {
			context: 'global',
			filters: {
				customer: {
					value: '746266',
					operator: '=',
					filterCondition: 'and',
				},
				status: {
					value: ['NEW', 'INVITED', 'COUNTERED', 'VIEWED', 'ACCEPTED', 'RESPONDED'],
					operator: '=',
					filterCondition: 'and',
				},
			},
			pageNumber: 0,
			pageSize: 25,
		};

		this.loadBids(testConfig);
	}

	// Debug method to log current state
	public debugLogState(): void {
		// Debug logging removed for production
	}

	// Global debug function for browser console testing
	public debugCompareWithPostman(): void {
		// Debug logging removed for production
	}

	// Public methods for uniqueBidIds access
	public getUniqueBidIds(): string[] {
		return [...this.uniqueBidIds];
	}

	public addUniqueBidId(bidId: string): void {
		if (!this.uniqueBidIds.includes(bidId)) {
			this.uniqueBidIds.push(bidId);
		}
	}

	public hasUniqueBidId(bidId: string): boolean {
		return this.uniqueBidIds.includes(bidId);
	}

	public getUniqueBidIdsCount(): number {
		return this.uniqueBidIds.length;
	}

	private addUserFilter(filtersList: any[], filters: any): void {
		if (!filters.user) {
			return;
		}

		const userFilter = filters.user;
		if (userFilter.value && Array.isArray(userFilter.value) && userFilter.value.length > 0) {
			filtersList.push({
				field: 'customerId',
				value: userFilter.value.join(','),
				operator: userFilter.operator || '=',
				startValue: userFilter.startValue,
				endValue: userFilter.endValue,
				condition: userFilter.filterCondition || 'and',
			});
		}
	}

	private addReferenceNumberFilter(filtersList: any[], filters: any): void {
		if (!filters.referenceNumber) {
			return;
		}

		const referenceNumberFilter = filters.referenceNumber;
		if (typeof referenceNumberFilter.value === 'string' && referenceNumberFilter.value.trim()) {
			filtersList.push({
				field: 'referenceNumber',
				value: referenceNumberFilter.value.trim(),
				operator: referenceNumberFilter.operator || '%',
				startValue: referenceNumberFilter.startValue,
				endValue: referenceNumberFilter.endValue,
				condition: referenceNumberFilter.filterCondition || 'and',
			});
		}
	}

	private addCarrierBusinessNameFilter(filtersList: any[], filters: any): void {
		if (!filters.carrierBusinessName) {
			return;
		}

		const carrierBusinessNameFilter = filters.carrierBusinessName;
		if (
			typeof carrierBusinessNameFilter.value === 'string' &&
			carrierBusinessNameFilter.value.trim()
		) {
			filtersList.push({
				field: 'carrierBusinessName',
				value: carrierBusinessNameFilter.value.trim(),
				operator: carrierBusinessNameFilter.operator || '%',
				startValue: carrierBusinessNameFilter.startValue,
				endValue: carrierBusinessNameFilter.endValue,
				condition: carrierBusinessNameFilter.filterCondition || 'and',
			});
		}
	}

	private addCarrierMcFilter(filtersList: any[], filters: any): void {
		if (!filters.carrierMc) {
			return;
		}

		const carrierMcFilter = filters.carrierMc;
		if (typeof carrierMcFilter.value === 'string' && carrierMcFilter.value.trim()) {
			filtersList.push({
				field: 'carrierMc',
				value: carrierMcFilter.value.trim(),
				operator: carrierMcFilter.operator || '%',
				startValue: carrierMcFilter.startValue,
				endValue: carrierMcFilter.endValue,
				condition: carrierMcFilter.filterCondition || 'and',
			});
		}
	}

	private addCarrierDotFilter(filtersList: any[], filters: any): void {
		if (!filters.carrierDot) {
			return;
		}

		const carrierDotFilter = filters.carrierDot;
		if (typeof carrierDotFilter.value === 'string' && carrierDotFilter.value.trim()) {
			filtersList.push({
				field: 'carrierDot',
				value: carrierDotFilter.value.trim(),
				operator: carrierDotFilter.operator || '%',
				startValue: carrierDotFilter.startValue,
				endValue: carrierDotFilter.endValue,
				condition: carrierDotFilter.filterCondition || 'and',
			});
		}
	}

	private addLoadRateFilter(filtersList: any[], filters: any): void {
		if (!filters.loadRate) {
			return;
		}

		const loadRateFilter = filters.loadRate;
		if (loadRateFilter.value !== null && loadRateFilter.value !== undefined) {
			filtersList.push({
				field: 'loadRate',
				value: loadRateFilter.value.toString(),
				operator: loadRateFilter.operator || '=',
				startValue: loadRateFilter.startValue,
				endValue: loadRateFilter.endValue,
				condition: loadRateFilter.filterCondition || 'and',
			});
		}
	}

	private addBidRateFilter(filtersList: any[], filters: any): void {
		if (!filters.bidRate) {
			return;
		}

		const bidRateFilter = filters.bidRate;
		if (bidRateFilter.value !== null && bidRateFilter.value !== undefined) {
			filtersList.push({
				field: 'bidRate',
				value: bidRateFilter.value.toString(),
				operator: bidRateFilter.operator || '=',
				startValue: bidRateFilter.startValue,
				endValue: bidRateFilter.endValue,
				condition: bidRateFilter.filterCondition || 'and',
			});
		}
	}

	private addEquipmentFilter(filtersList: any[], filters: any): void {
		if (!filters.equipment) {
			return;
		}

		const equipmentFilter = filters.equipment;
		if (typeof equipmentFilter.value === 'string' && equipmentFilter.value.trim()) {
			filtersList.push({
				field: 'equipment',
				value: equipmentFilter.value.trim(),
				operator: equipmentFilter.operator || '=',
				startValue: equipmentFilter.startValue,
				endValue: equipmentFilter.endValue,
				condition: equipmentFilter.filterCondition || 'and',
			});
		}
	}

	private addCreatedDateFilter(filtersList: any[], filters: any): void {
		if (!filters.createdDate) {
			return;
		}

		const createdDateFilter = filters.createdDate;
		// Handle date range filtering - if startValue and endValue are provided, use 'between' operator
		if (createdDateFilter.startValue && createdDateFilter.endValue) {
			filtersList.push({
				field: 'createdDate',
				value: createdDateFilter.value || '',
				operator: 'between',
				startValue: createdDateFilter.startValue,
				endValue: createdDateFilter.endValue,
				condition: createdDateFilter.filterCondition || 'and',
			});
		} else if (typeof createdDateFilter.value === 'string' && createdDateFilter.value.trim()) {
			// Handle single date value
			filtersList.push({
				field: 'createdDate',
				value: createdDateFilter.value.trim(),
				operator: createdDateFilter.operator || '=',
				startValue: createdDateFilter.startValue,
				endValue: createdDateFilter.endValue,
				condition: createdDateFilter.filterCondition || 'and',
			});
		}
	}

	private addDeliveryDateFilter(filtersList: any[], filters: any): void {
		if (!filters.deliveryDate) {
			return;
		}

		const deliveryDateFilter = filters.deliveryDate;
		// Handle date range filtering - if startValue and endValue are provided, use 'between' operator
		if (deliveryDateFilter.startValue && deliveryDateFilter.endValue) {
			filtersList.push({
				field: 'deliveryDate',
				value: deliveryDateFilter.value || '',
				operator: 'between',
				startValue: deliveryDateFilter.startValue,
				endValue: deliveryDateFilter.endValue,
				condition: deliveryDateFilter.filterCondition || 'and',
			});
		} else if (typeof deliveryDateFilter.value === 'string' && deliveryDateFilter.value.trim()) {
			// Handle single date value
			filtersList.push({
				field: 'deliveryDate',
				value: deliveryDateFilter.value.trim(),
				operator: deliveryDateFilter.operator || '=',
				startValue: deliveryDateFilter.startValue,
				endValue: deliveryDateFilter.endValue,
				condition: deliveryDateFilter.filterCondition || 'and',
			});
		}
	}

	private addPickupDateFilter(filtersList: any[], filters: any): void {
		if (!filters.pickupDate) {
			return;
		}

		const pickupDateFilter = filters.pickupDate;
		// Handle date range filtering - if startValue and endValue are provided, use 'between' operator
		if (pickupDateFilter.startValue && pickupDateFilter.endValue) {
			filtersList.push({
				field: 'pickupDate',
				value: pickupDateFilter.value || '',
				operator: 'between',
				startValue: pickupDateFilter.startValue,
				endValue: pickupDateFilter.endValue,
				condition: pickupDateFilter.filterCondition || 'and',
			});
		} else if (typeof pickupDateFilter.value === 'string' && pickupDateFilter.value.trim()) {
			// Handle single date value
			filtersList.push({
				field: 'pickupDate',
				value: pickupDateFilter.value.trim(),
				operator: pickupDateFilter.operator || '=',
				startValue: pickupDateFilter.startValue,
				endValue: pickupDateFilter.endValue,
				condition: pickupDateFilter.filterCondition || 'and',
			});
		}
	}

	// Re-fetch saved views (tabs)
	public readonly getUserSortsAndFilters = this.effect(
		(trigger$: Observable<BidListConfig | { context: 'global' } | null | void>) => {
			return trigger$.pipe(
				filter((cfg) => !cfg || (cfg as any).context === 'global'),
				exhaustMap(() =>
					this.restService
						.POST(this.urlService.getUserSortsAndFilters(), { type: FILTER_TYPES.OFFERS })
						.pipe(
							map((resp: any) => resp?.data ?? resp),
							tap((raw: any[] = []) => {
								const normalized = (raw || []).map((sf: any) => ({
									...sf,
									filter: typeof sf?.filter === 'string' ? JSON.parse(sf.filter) : sf?.filter,
									sort: typeof sf?.sort === 'string' ? JSON.parse(sf.sort) : sf?.sort,
								}));

								const tabs = [OFFER_TAB_TYPES.ACTIVE];
								(normalized || []).forEach((sf) => {
									if (sf?.name && sf.name !== OFFER_TAB_TYPES.ACTIVE) tabs.push(sf.name);
								});

								this.patchState((state) => ({
									...state,
									allTabs: tabs,
									userSortAndFilters: normalized,
								}));

								// ensure active tab is valid
								const active = this.get((s) => s.activeTab);
								if (!tabs.includes(active)) this.setActiveTab(OFFER_TAB_TYPES.ACTIVE);
							}),
							catchError(() => EMPTY)
						)
				)
			);
		}
	);

	// Delete a saved view then refresh tabs
	public readonly deleteCustomTab = this.effect(
		(customTabId$: Observable<number | string | null | undefined>) => {
			return customTabId$.pipe(
				map((id) => (typeof id === 'string' ? Number(id) : id)),
				filter((id): id is number => typeof id === 'number' && Number.isFinite(id) && id > 0),
				exhaustMap((id) =>
					this.restService.DELETE(this.urlService.deleteUserSortsAndFilters(id)).pipe(
						tap(() => {
							// Refresh tabs list
							this.getUserSortsAndFilters(of(void 0));

							// Reset tab state
							this.setActiveTab(OFFER_TAB_TYPES.ACTIVE);
							this.setPageNumber(0);
							this.setCustomTabId(null); // now valid

							// Notify OffersList
							this._customTabDeleted.next();
						}),
						catchError(() => EMPTY)
					)
				)
			);
		}
	);

	public readonly setCustomTabId = this.updater((state, customTabId: number | null) => ({
		...state,
		customTabId,
	}));

	public readonly setActiveTab = this.updater((state, tab: string) => ({
		...state,
		activeTab: tab,
	}));

	// CN-8620: store the quote bid list status filter so it sticks across pagination.
	public readonly setQuoteStatusFilter = this.updater(
		(state, quoteStatusFilter: QuoteStatusFilter) => ({
			...state,
			quoteStatusFilter,
		})
	);
}
